import { Component, OnDestroy, forwardRef, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { HardBreak } from '@tiptap/extension-hard-break';

@Component({
  selector: 'app-tiptap-editor',
  templateUrl: './tiptap-editor.component.html',
  styleUrls: ['./tiptap-editor.component.scss'],
  standalone: false,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TiptapEditorComponent),
    multi: true
  }]
})
export class TiptapEditorComponent implements OnDestroy, AfterViewInit, ControlValueAccessor {
  @ViewChild('editorElement', { static: false }) editorElement!: ElementRef;
  editor!: Editor;

  constructor(private cdr: ChangeDetectorRef) {}

  showLinkModal = false;
  currentLinkUrl = '';
  currentLinkText = '';
  linkType: 'url' | 'mailto' | 'tel' = 'url';
  linkUrlError = '';
  private _linkAnchor: HTMLAnchorElement | null = null;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private initialContent = '';

  ngAfterViewInit() {
    this.editor = new Editor({
      element: this.editorElement.nativeElement,
      extensions: [
        StarterKit.configure({
          hardBreak: false,
          link: false,
          underline: false,
        }),
        HardBreak.extend({
          addKeyboardShortcuts() {
            return { 'Shift-Enter': () => this.editor.commands.setHardBreak() };
          },
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { target: null, rel: null }
        }),
        Underline,
        Subscript,
        Superscript,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
      ],
      content: this.initialContent,
      onUpdate: ({ editor }) => this.onChange(editor.getHTML()),
      onBlur: () => this.onTouched(),
    });
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.editor?.destroy();
  }

  writeValue(value: string): void {
    if (this.editor) {
      if (value !== this.editor.getHTML()) {
        this.editor.commands.setContent(value || '');
      }
    } else {
      this.initialContent = value || '';
    }
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.editor?.setEditable(!isDisabled); }

  toggleBold() { this.editor.chain().focus().toggleBold().run(); }
  toggleItalic() { this.editor.chain().focus().toggleItalic().run(); }
  toggleUnderline() { this.editor.chain().focus().toggleUnderline().run(); }
  toggleBulletList() { this.editor.chain().focus().toggleBulletList().run(); }
  toggleOrderedList() { this.editor.chain().focus().toggleOrderedList().run(); }
  toggleBlockquote() { this.editor.chain().focus().toggleBlockquote().run(); }
  toggleSubscript() { this.editor.chain().focus().toggleSubscript().run(); }
  toggleSuperscript() { this.editor.chain().focus().toggleSuperscript().run(); }
  clearFormatting() { this.editor.chain().focus().clearNodes().unsetAllMarks().run(); }

  setTextAlign(align: string) { this.editor.chain().focus().setTextAlign(align).run(); }

  setHeading(level: number) {
    if (level === 0) {
      this.editor.chain().focus().setParagraph().run();
    } else {
      this.editor.chain().focus().toggleHeading({ level: level as any }).run();
    }
  }

  toggleLink() {
    if (this.editor.isActive('link')) {
      const { from } = this.editor.state.selection;
      const domAtPos = this.editor.view.domAtPos(from);
      const node = domAtPos.node as HTMLElement;
      const anchor = node.nodeType === Node.TEXT_NODE ? node.parentElement?.closest('a') : node.closest('a');
      if (anchor) this.openLinkEditor(anchor as HTMLAnchorElement);
    } else {
      this._linkAnchor = null;
      const { from, to } = this.editor.state.selection;
      this.currentLinkText = this.editor.state.doc.textBetween(from, to, '');
      this.currentLinkUrl = '';
      this.linkType = 'url';
      this.linkUrlError = '';
      this.showLinkModal = true;
    }
  }

  openLinkEditor(linkElement: HTMLAnchorElement) {
    this._linkAnchor = linkElement;
    const href = linkElement.getAttribute('href') || '';
    if (href.startsWith('mailto:')) {
      this.linkType = 'mailto';
      this.currentLinkUrl = href.replace('mailto:', '');
    } else if (href.startsWith('tel:')) {
      this.linkType = 'tel';
      this.currentLinkUrl = href.replace('tel:', '');
    } else {
      this.linkType = 'url';
      this.currentLinkUrl = href;
    }
    this.currentLinkText = linkElement.textContent || '';
    this.linkUrlError = '';
    this.showLinkModal = true;
  }

  onLinkTypeChange() {
    this.linkUrlError = '';
    this.currentLinkUrl = this.currentLinkUrl
      .replace(/^https?:\/\//, '')
      .replace(/^mailto:/, '')
      .replace(/^tel:/, '');
  }

  get linkInputLabel(): string {
    return this.linkType === 'mailto' ? 'Email' : this.linkType === 'tel' ? 'Phone Number' : 'URL';
  }

  get linkInputPlaceholder(): string {
    return this.linkType === 'mailto' ? 'email@example.com' : this.linkType === 'tel' ? '+44 1234 567890' : 'https://example.com';
  }

  private validateUrl(): boolean {
    this.linkUrlError = '';
    const url = this.currentLinkUrl.trim();
    if (!url) { this.linkUrlError = this.linkInputLabel + ' is required'; return false; }
    if (this.linkType === 'mailto') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url)) { this.linkUrlError = 'Please enter a valid email address'; return false; }
      return true;
    }
    if (this.linkType === 'tel') {
      if (!/^[+\d][\d\s\-().]+$/.test(url)) { this.linkUrlError = 'Please enter a valid phone number'; return false; }
      return true;
    }
    if (!/^https?:\/\//.test(url)) this.currentLinkUrl = 'https://' + url;
    if (!/^https?:\/\/[^\s]+\.[^\s]+/.test(this.currentLinkUrl.trim())) {
      this.linkUrlError = 'Please enter a complete URL (e.g. https://example.com)';
      return false;
    }
    return true;
  }

  private buildHref(): string {
    const url = this.currentLinkUrl.trim();
    if (this.linkType === 'mailto') return 'mailto:' + url;
    if (this.linkType === 'tel') return 'tel:' + url.replace(/[\s\-().]/g, '');
    return url;
  }

  saveLink() {
    if (!this.validateUrl()) return;
    const newHref = this.buildHref();
    const newText = this.currentLinkText.trim() || this.currentLinkUrl.trim();

    if (!this._linkAnchor) {
      const { from, empty } = this.editor.state.selection;
      if (empty) {
        const linkMark = this.editor.schema.marks['link'].create({ href: newHref });
        const textNode = this.editor.schema.text(newText, [linkMark]);
        this.editor.view.dispatch(this.editor.state.tr.insert(from, textNode));
      } else {
        this.editor.chain().focus().setLink({ href: newHref }).run();
      }
      this.showLinkModal = false;
      return;
    }

    const oldText = this._linkAnchor.textContent || '';
    const { state } = this.editor;
    let linkFrom: number | null = null;
    let linkTo: number | null = null;

    state.doc.descendants((node: any, pos: any): boolean => {
      if (linkFrom !== null) return false;
      if (node.isText) {
        const lm = node.marks.find((m: any) => m.type.name === 'link' && m.attrs.href === this._linkAnchor?.getAttribute('href'));
        if (lm && node.text === oldText) { linkFrom = pos; linkTo = pos + node.nodeSize; return false; }
      }
      return true;
    });

    if (linkFrom !== null && linkTo !== null) {
      const linkMark = this.editor.schema.marks['link'].create({ href: newHref });
      const textNode = this.editor.schema.text(newText, [linkMark]);
      this.editor.view.dispatch(state.tr.replaceWith(linkFrom, linkTo, textNode));
    }

    this.showLinkModal = false;
    this._linkAnchor = null;
  }

  removeLink() {
    if (this._linkAnchor) {
      const oldText = this._linkAnchor.textContent || '';
      this.editor.state.doc.descendants((node: any, pos: any): boolean => {
        if (node.isText && node.marks.some((m: any) => m.type.name === 'link') && node.text === oldText) {
          this.editor.chain().setTextSelection({ from: pos, to: pos + node.nodeSize }).unsetLink().run();
          return false;
        }
        return true;
      });
    }
    this.showLinkModal = false;
    this._linkAnchor = null;
  }

  closeLinkModal() {
    this.showLinkModal = false;
    this._linkAnchor = null;
  }
}
